// The MEMORY ARTICLE reader (Signal re-layout) — the design's long-form column over ONE Memory Node.
// READ-ONLY: no raw-edit/delete/save control exists here (N-MEM-1 AC-3); the only affordances are navigation
// (a neighbour) and a chat handoff (the human asks about the memory in their own words — the human still
// sends it). A `[[wikilink]]`, a backlink and a tag render exactly as `memory` recorded them.
//
// WHAT THE DESIGN SHOWS THAT THE DATA DOES NOT SUPPORT, and is therefore ABSENT here:
//   • the LEDE and the "What Ritona keeps in mind" block — a Node carries ONE markdown `body`; neither a
//     summary nor a takeaway exists to render, and writing one here would be this surface inventing content;
//   • the importance METER and its percentage — `importance` is an ENUM (business_critical … background),
//     so the level is printed as words; a 0–100 bar derived from five names would be a fabricated figure;
//   • "cited N× this month · last in …" — there is no usage or last-used signal anywhere in the projection;
//   • the maturity pill (CORE / VALIDATED / UNCONFIRMED) — not a field. The pill slot instead carries the
//     one real lifecycle state a Node has: SUPERSEDED, when `supersededBy` is set (R-MEM-7, retire-by-supersede).
// The article's SECTIONS are real: they are the Node's own markdown, rendered as headings/prose/lists/tables.

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StatusPill } from '../primitives.tsx';
import { Chip } from './know-primitives.tsx';
import { Icon } from '../../app/Icon.tsx';
import { areaOfPath, humaniseEnum } from './memory-tree.ts';
import type { MemoryEdge } from './MemoryConnectionMap.tsx';
import type { MemoryNodeView } from '../know-projections.ts';

export interface MemoryArticleProps {
  readonly node: MemoryNodeView;
  /** The focused Node's real edges (outbound `[[wikilinks]]` + inbound backlinks). */
  readonly edges: readonly MemoryEdge[];
  readonly onFocusNode: (nodeId: string) => void;
  /** Hand the memory to chat as a drafted question — the human types and sends; nothing is written here. */
  readonly onAsk: () => void;
}

/** The area trail above the title, from the Node's own repo-relative path. Empty at the wiki root. */
function crumbOf(path: string): string {
  return areaOfPath(path).split('/').filter(Boolean).join(' · ');
}

/** The header block: the area trail, the chat handoff, the title, and the meta the frontmatter carries. */
function ArticleHead({
  node,
  onAsk,
}: {
  readonly node: MemoryNodeView;
  readonly onAsk: () => void;
}): React.JSX.Element {
  const frontmatter = node.frontmatter;
  const crumb = crumbOf(node.path);
  return (
    <>
      <div className="mw-art-top">
        {crumb.length > 0 ? <span className="mw-crumb">{crumb}</span> : null}
        <span className="spacer" aria-hidden="true" />
        <button type="button" className="mw-ask" onClick={onAsk}>
          <Icon name="chat" size={13} /> Ask about this memory
        </button>
      </div>
      <h2 className="mw-title">{frontmatter.title}</h2>
      <div className="mw-meta">
        {frontmatter.supersededBy !== null ? (
          <StatusPill tone="attention">Superseded</StatusPill>
        ) : null}
        {frontmatter.lastVerified.length > 0 ? (
          <span>Last verified {frontmatter.lastVerified}</span>
        ) : null}
        {frontmatter.importance.length > 0 ? (
          <span>Importance {humaniseEnum(frontmatter.importance).toLowerCase()}</span>
        ) : null}
        {frontmatter.period !== null ? <span>{frontmatter.period}</span> : null}
      </div>
    </>
  );
}

/** One navigable neighbour card. The arrow and relation are decorative, so the button's accessible name
 *  stays the neighbour's own label — the thing the reader is actually navigating to. */
function ConnectionCard({
  edge,
  onFocusNode,
}: {
  readonly edge: MemoryEdge;
  readonly onFocusNode: (nodeId: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="mw-conn"
      data-neighbour={edge.key}
      onClick={() => onFocusNode(edge.nodeId)}
    >
      <span className="top">
        <span className="dir" aria-hidden="true">
          {edge.direction === 'out' ? '→' : '←'}
        </span>
        <span className="nm">{edge.label}</span>
      </span>
      <span className="rel" aria-hidden="true">
        {edge.relation}
      </span>
    </button>
  );
}

/** The connected-memories grid — the design's region, rendered from the real edge list. */
function ConnectionGrid({
  edges,
  onFocusNode,
}: {
  readonly edges: readonly MemoryEdge[];
  readonly onFocusNode: (nodeId: string) => void;
}): React.JSX.Element {
  return (
    <section className="mw-sec" data-neighbours="connections">
      <div className="mono-label">Connected memories · {edges.length}</div>
      {/* The list key carries the index because `edge.key` is the TARGET marker, and `memory` de-duplicates
          `[[wikilinks]]` by (relation, target) — so one Node may legitimately link to the same target under
          two relations (`[[cites:x]]` and `[[supersedes:x]]`) and produce two edges with the same marker. */}
      <div className="mw-conns">
        {edges.map((edge, index) => (
          <ConnectionCard
            key={`${edge.key}-${String(index)}`}
            edge={edge}
            onFocusNode={onFocusNode}
          />
        ))}
      </div>
    </section>
  );
}

/** The article — head, the Node's own markdown, its tags, its connections, and where it came from. */
export function MemoryArticle({
  node,
  edges,
  onFocusNode,
  onAsk,
}: MemoryArticleProps): React.JSX.Element {
  const frontmatter = node.frontmatter;
  return (
    <div className="mw-article">
      <article className="mw-art-inner" data-memory-article={node.id}>
        <ArticleHead node={node} onAsk={onAsk} />
        <div className="mw-rule" />
        <div className="mw-prose" data-memory-body>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{node.body}</ReactMarkdown>
        </div>
        {frontmatter.tags.length > 0 ? (
          <div className="mw-tags">
            {frontmatter.tags.map((tag) => (
              <Chip key={tag}>{tag}</Chip>
            ))}
          </div>
        ) : null}
        {edges.length > 0 ? <ConnectionGrid edges={edges} onFocusNode={onFocusNode} /> : null}
        {frontmatter.provenance.length > 0 ? (
          <div className="mw-source">SOURCE — {frontmatter.provenance}</div>
        ) : null}
      </article>
    </div>
  );
}
